<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EvolutionGroupSettingsDialog from './EvolutionGroupSettingsDialog.vue';

const props = defineProps({
  contact: { type: Object, required: true },
  conversationId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const store = useStore();

// Cached from the group Contact record - avoids an eager Evolution API call every time this
// panel opens. The live name/description/picture/member list are only fetched when the agent
// actually opens "Manage group" below.
const membersCount = ref(
  props.contact.additional_attributes?.evolution_participants_count ?? null
);
const settingsDialogRef = ref(null);

const membersCountLabel = computed(() => {
  if (membersCount.value === null)
    return t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBERS');
  return `${t('CONTACT_PANEL.EVOLUTION_GROUP.MEMBERS')} (${membersCount.value})`;
});

// Keeps the cached count (Contact#additional_attributes.evolution_participants_count) honest
// after add/remove actions taken through Chatwoot, so the next time this panel mounts it still
// shows a reasonably fresh number without needing a live fetch.
const onMembersCountChanged = async count => {
  membersCount.value = count;
  try {
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      additional_attributes: {
        ...(props.contact.additional_attributes || {}),
        evolution_participants_count: count,
      },
    });
  } catch (error) {
    // Cache going stale here is harmless - it just self-corrects next time someone reopens
    // the settings dialog, which always fetches the live count from Evolution.
  }
};
</script>

<template>
  <div
    class="flex items-center justify-between w-full pt-3 mt-1 border-t border-n-weak"
  >
    <span class="text-sm font-medium text-n-slate-12">
      {{ membersCountLabel }}
    </span>
    <NextButton
      icon="i-lucide-settings-2"
      :label="t('CONTACT_PANEL.EVOLUTION_GROUP.MANAGE_GROUP')"
      slate
      faded
      xs
      @click="settingsDialogRef?.open()"
    />
  </div>

  <EvolutionGroupSettingsDialog
    ref="settingsDialogRef"
    :contact="contact"
    :conversation-id="conversationId"
    @members-changed="onMembersCountChanged"
  />
</template>
